import Big from "big.js";
import moment from "moment";
import { EmployeePosition, FormMode } from "enums";
import { removeComasStringAmount } from "utils/text.utils";
import { validateTotalizers } from "utils/form-data/objects.utils";

export const ShiftDetailsTab = {
  Info: "info",
  WetStock: "wet-stock-inventory",
  DryStock: "dry-stock-inventory",
  CashAndNonCash: "cash-noncash",
};

const INFO_FORM_FIELDS = ["shiftDate", "shiftNumber"];

const UNCOUNTED_FORM_FIELDS = ["shiftPumpTotals"];

const TOLERATED_SHORTAGE = 5;

const EMPLOYEE_CHECKS = [
  { check: "nameCheck", field: "name" },
  { check: "agencyCheck", field: "agency" },
  { check: "labelCheck", field: "label" },
  { check: "timeInAtCheck", field: "timeInAt" },
  { check: "timeOutAtCheck", field: "timeOutAt" },
];

const isVarianceEmployee = (employee) => employee?.name?.toLowerCase() === "stocq variance";

const isBlank = (value) => !value || value.length === 0;

export const fieldAnchorId = (name) => `sr-field-${name}`;
export const employeeAnchorId = (index, field) => `sr-employee-${index}-${field}`;
export const totalizerAnchorId = (fuelingPointId, productName, isStart) =>
  `sr-totalizer-${String(fuelingPointId).replace(/\s+/g, "")}-${productName}-${
    isStart ? "start" : "end"
  }`;
export const safeDropAnchorId = (key) => `sr-safedrop-${key}`;
export const lpgSafeDropAnchorId = (key) => `sr-safedrop-lpg-${key}`;
export const employeeToPayAnchorId = (index) => `sr-employee-to-pay-${index}`;
export const CASH_VARIANCE_ANCHOR_ID = "sr-cash-variance";

const getTotalizerProductNames = (pumpTotal) => {
  const productNames = new Set();
  Object.keys(pumpTotal).forEach((key) => {
    if (key.startsWith("startTotalizer") && !key.toLowerCase().includes("old")) {
      productNames.add(key.replace("startTotalizer", ""));
    }
  });
  return Array.from(productNames);
};

const getIncompleteTotalizers = (pumpTotals = []) => {
  const incomplete = [];

  pumpTotals.forEach((pumpTotal) => {
    getTotalizerProductNames(pumpTotal).forEach((productName) => {
      const rawStart = pumpTotal[`startTotalizer${productName}`];
      const rawEnd = pumpTotal[`endTotalizer${productName}`];

      const startIsBlank = rawStart === "" || rawStart === null || rawStart === undefined;
      const endIsBlank = rawEnd === "" || rawEnd === null || rawEnd === undefined;

      if (startIsBlank) {
        incomplete.push({
          anchorId: totalizerAnchorId(pumpTotal.fuelingPointId, productName, true),
        });
      }

      if (endIsBlank) {
        incomplete.push({
          anchorId: totalizerAnchorId(pumpTotal.fuelingPointId, productName, false),
        });
        return;
      }

      if (startIsBlank) {
        return;
      }

      const start = removeComasStringAmount(rawStart);
      const end = removeComasStringAmount(rawEnd);

      if (Number(start) < 0) {
        incomplete.push({
          anchorId: totalizerAnchorId(pumpTotal.fuelingPointId, productName, true),
        });
      }

      if (Number(end) < 0 || Big(start).gt(end)) {
        incomplete.push({
          anchorId: totalizerAnchorId(pumpTotal.fuelingPointId, productName, false),
        });
      }
    });
  });

  return incomplete;
};

export const isShoulderRequired = (variance) =>
  Big(variance).lt(0) && Big(Math.abs(variance)).gt(TOLERATED_SHORTAGE);

export const computeCashVariance = ({ totalSafeDrop = 0, cashOnHand = 0 }) =>
  Number(
    (Big(cashOnHand).lt(0)
      ? Big(totalSafeDrop).add(cashOnHand)
      : Big(totalSafeDrop).minus(cashOnHand)
    ).round(2),
  );

export const isShortageCovered = ({ variance, totalShoulderAmount = 0 }) =>
  Big(Math.abs(variance)).lte(totalShoulderAmount) && totalShoulderAmount !== 0;

export const employeeShoulderKey = (employee, index) =>
  employee?.shiftEmployeeId != null ? String(employee.shiftEmployeeId) : `new__${index}`;

const hasSelectableEmployees = (employees = []) =>
  employees.some((e) => e.name !== "" && e.agency !== "" && e.label !== "");

export const canEditTotalizers = (role) =>
  [EmployeePosition.STATION_HEAD, "portal-admin"].includes(role);

export const isEndingDipstickEnabled = ({ fields = {}, role }) => {
  if (fields?.isReadingDipstickEnding?.value === true) {
    return false;
  }
  const pumpTotals = fields?.shiftPumpTotals?.value || [];
  if (!canEditTotalizers(role) || pumpTotals.length === 0) {
    return true;
  }
  return validateTotalizers(pumpTotals);
};

const dipstickProduct = (name) => name.replace(/^(beginning|ending)/, "").replace(/Cm$/, "");

export const isBeginningDipstickEnabled = ({ name, fields = {}, recentShift }) => {
  if (fields?.isReadingDipstickBeginning?.value === true) {
    return false;
  }
  if (!recentShift) {
    return true;
  }
  const product = dipstickProduct(name);
  return (
    recentShift.isReadingDipstickEnding !== false &&
    recentShift[`ending${product}Cm`] === 0 &&
    recentShift[`ending${product}`] === 0
  );
};

export const getIncompleteFieldsByTab = ({
  pageMode,
  fields = {},
  employees = [],
  employeesToPay = [],
  safeDrop = [],
  variance = 0,
  totalShoulderAmount = 0,
  recentShift = null,
  role,
}) => {
  const byTab = {
    [ShiftDetailsTab.Info]: [],
    [ShiftDetailsTab.WetStock]: [],
    [ShiftDetailsTab.DryStock]: [],
    [ShiftDetailsTab.CashAndNonCash]: [],
  };

  if (pageMode !== FormMode.Edit) {
    return byTab;
  }

  const endingDipstickEnabled = isEndingDipstickEnabled({ fields, role });

  Object.entries(fields).forEach(([name, field]) => {
    if (!field || UNCOUNTED_FORM_FIELDS.includes(name) || field.disabled) {
      return;
    }

    if (!endingDipstickEnabled && name.toLowerCase().includes("ending")) {
      return;
    }

    if (
      name.startsWith("beginning") &&
      !isBeginningDipstickEnabled({ name, fields, recentShift })
    ) {
      return;
    }

    const isMissing = field.required && isBlank(field.value);
    if (!isMissing && !field.error) {
      return;
    }

    const tab = INFO_FORM_FIELDS.includes(name) ? ShiftDetailsTab.Info : ShiftDetailsTab.WetStock;
    byTab[tab].push({ anchorId: fieldAnchorId(name) });
  });

  const shiftDate = fields?.shiftDate;
  const isShiftDateFuture =
    shiftDate?.value && moment(shiftDate.value).isAfter(moment().endOf("day"));
  if (isShiftDateFuture && !isBlank(shiftDate.value) && !shiftDate.error) {
    byTab[ShiftDetailsTab.Info].push({ anchorId: fieldAnchorId("shiftDate") });
  }

  employees.forEach((employee, index) => {
    if (isVarianceEmployee(employee)) {
      return;
    }
    EMPLOYEE_CHECKS.forEach(({ check, field }) => {
      if (employee[check]) {
        byTab[ShiftDetailsTab.Info].push({ anchorId: employeeAnchorId(index, field) });
      }
    });
  });

  const pumpTotals = fields?.shiftPumpTotals?.value || [];
  if (canEditTotalizers(role) && pumpTotals.length > 0) {
    byTab[ShiftDetailsTab.WetStock].push(...getIncompleteTotalizers(pumpTotals));
  }

  safeDrop.forEach((row) => {
    if (row.error) {
      byTab[ShiftDetailsTab.CashAndNonCash].push({ anchorId: safeDropAnchorId(row.key) });
    }
    if (row.lpgError) {
      byTab[ShiftDetailsTab.CashAndNonCash].push({ anchorId: lpgSafeDropAnchorId(row.key) });
    }
  });

  if (isShoulderRequired(variance)) {
    if (hasSelectableEmployees(employees)) {
      employeesToPay.forEach((row, index) => {
        if (isBlank(row?.value)) {
          byTab[ShiftDetailsTab.CashAndNonCash].push({ anchorId: employeeToPayAnchorId(index) });
        }
      });
    }

    if (!isShortageCovered({ variance, totalShoulderAmount })) {
      byTab[ShiftDetailsTab.CashAndNonCash].push({ anchorId: CASH_VARIANCE_ANCHOR_ID });
    }
  }

  return byTab;
};

export const countIncompleteFields = (byTab) =>
  Object.values(byTab).reduce((total, entries) => total + entries.length, 0);

export const isFormFieldIncomplete = (field) =>
  Boolean(field) && !field.disabled && ((field.required && isBlank(field.value)) || field.error);
